'use client'

import { useQuery } from '@tanstack/react-query'

interface RedstonePrice {
	symbol: string
	value: number
}

interface CryptoPrices {
	BTC: number | null
	ETH: number | null
	SOL: number | null
}

async function fetchPrice(symbol: string): Promise<number | null> {
	try {
		const res = await fetch(
			`https://api.redstone.finance/prices/?symbol=${symbol}&provider=redstone&limit=1`
		)
		if (!res.ok) return null
		const data: RedstonePrice[] = await res.json()
		return data?.[0]?.value ?? null
	} catch {
		return null
	}
}

async function fetchCryptoPrices(): Promise<CryptoPrices> {
	const [btc, eth, sol] = await Promise.all([
		fetchPrice('BTC'),
		fetchPrice('ETH'),
		fetchPrice('SOL'),
	])
	return { BTC: btc, ETH: eth, SOL: sol }
}

export function useCryptoPrices() {
	return useQuery({
		queryKey: ['crypto-prices'],
		queryFn: fetchCryptoPrices,
		refetchInterval: 60_000,
		staleTime: 30_000,
		placeholderData: { BTC: null, ETH: null, SOL: null },
	})
}

export function formatPrice(value: number | null | undefined): string {
	if (value == null) return '—'
	if (value >= 1000) {
		return value.toLocaleString('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
	}
	return value.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}
